import type { ReactNode } from "react";

/**
 * The top of a screen: what it is called, what it is for, and the way in.
 *
 * One component because there were four, and they disagreed about everything
 * measurable — the title was `text-2xl` on three screens and `text-xl` on the
 * fourth, the block was aligned to its centre here and to its top there, the
 * padding above it was 20px, 16px or 4px depending on where you had come
 * from, and one screen had no strapline at all. Switching tabs made the «+»
 * hop around, which is the sort of thing nobody names and everybody feels.
 *
 * **The strapline is not optional**, and that is the whole mechanism: it is
 * what fixes the height of this block, and a fixed height is what keeps the
 * button still. A screen with nothing to say under its name was not a screen
 * that saved a line — it was a screen whose «+» sat twenty pixels above
 * everybody else's.
 *
 * The action, by contrast, may be absent: the lists screen hides it while it
 * is empty (the empty state offers the same door with words on it) and the
 * calendar hides it while a form is open. Nothing moves when it goes, because
 * nothing was depending on it for height.
 */
export function ScreenHeader({
  title,
  subtitle,
  children,
  action,
}: {
  title: string;
  /** One line about what this screen is for. Always shown — see above. */
  subtitle: string;
  /** Controls that belong to this screen, between the title and the action. */
  children?: ReactNode;
  /** Usually the «+». Optional, and its absence changes no dimension. */
  action?: ReactNode;
}) {
  return (
    <header className="flex shrink-0 items-start gap-3 px-1 pt-1 pb-3">
      <div className="min-w-0 flex-1">
        <h1 className="font-display truncate text-2xl leading-tight">{title}</h1>
        <p className="text-muted mt-0.5 truncate text-[0.875rem]">{subtitle}</p>
      </div>

      {/* Centred against each other, not against the top of the header: the
          «+» is a 44px circle and a segmented switch beside it is about
          thirty, so aligning both to the same edge leaves their middles a
          finger's width apart. */}
      <div className="flex shrink-0 items-center gap-2">
        {children}
        {action}
      </div>
    </header>
  );
}
